import { type ReactNode } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { BrandMark } from '@rynora/theme';
import { ArrowLeft, AlertTriangle } from 'lucide-react';

interface LegalLayoutProps {
  /** Page heading (e.g. "Privacy Policy"). */
  title: string;
  /** Last reviewed date — surfaces under the title so users know when content was last verified. */
  updated: string;
  /** Optional one-line summary rendered above the body. */
  summary?: string;
  /**
   * If `true`, renders a yellow [REVIEW BEFORE GOING LIVE] banner at the top of
   * the body. Anything we shipped without legal review should flip this on so
   * the team can spot it. Default `true` for now — every legal page is template
   * copy until a lawyer signs off.
   */
  needsReview?: boolean;
  children: ReactNode;
}

const LEGAL_NAV = [
  { to: '/trust', label: 'Trust Center' },
  { to: '/privacy', label: 'Privacy' },
  { to: '/privacy/data-request', label: 'Data requests' },
  { to: '/terms', label: 'Terms' },
  { to: '/cookies', label: 'Cookies' },
  { to: '/security', label: 'Security' },
  { to: '/contact', label: 'Contact' },
];

const TRUST_SUBNAV = [
  { to: '/trust', label: 'Overview' },
  { to: '/trust/subprocessors', label: 'Sub-processors' },
  { to: '/trust/regions', label: 'Regions & residency' },
  { to: '/trust/ai', label: 'AI Bill of Materials' },
  { to: '/trust/sla', label: 'SLA & credits' },
  { to: '/trust/deprecation', label: 'Deprecation policy' },
];

/**
 * Shared chrome for /privacy, /terms, /cookies, /security, /contact.
 * Renders a slim header with BrandMark + back-to-home, a left rail with
 * the legal sub-nav (mobile collapses to top), and the content body.
 */
export function LegalLayout({ title, updated, summary, needsReview = true, children }: LegalLayoutProps) {
  const { pathname } = useLocation();

  return (
    <div className="legal-shell">
      <header className="legal-header">
        <div className="container legal-header-inner">
          <Link to="/" className="nav-logo" aria-label="Rynora — home">
            <BrandMark variant="compact" />
          </Link>
          <div className="legal-header-actions">
            <Link to="/" className="nav-auth-btn">
              <ArrowLeft size={14} /> Home
            </Link>
          </div>
        </div>
      </header>

      <div className="container legal-body">
        <aside className="legal-sidebar" aria-label="Legal pages">
          <nav>
            <ul>
              {LEGAL_NAV.map((item) => {
                const isTrust = item.to === '/trust';
                const isOnTrust = pathname === '/trust' || pathname.startsWith('/trust/');
                const isActive = isTrust ? isOnTrust : pathname === item.to;
                return (
                  <li key={item.to}>
                    <Link to={item.to} className={isActive ? 'is-active' : ''}>
                      {item.label}
                    </Link>
                    {isTrust && isOnTrust && (
                      <ul className="legal-sidebar-sub">
                        {TRUST_SUBNAV.map((sub) => (
                          <li key={sub.to}>
                            <Link to={sub.to} className={pathname === sub.to ? 'is-active' : ''}>
                              {sub.label}
                            </Link>
                          </li>
                        ))}
                      </ul>
                    )}
                  </li>
                );
              })}
            </ul>
          </nav>
          <p className="legal-sidebar-note">
            Questions? <a href="mailto:support@rynora.app">support@rynora.app</a>
          </p>
        </aside>

        <article className="legal-article">
          <header className="legal-article-head">
            <h1>{title}</h1>
            <p className="legal-meta">Last reviewed {updated}</p>
            {summary && <p className="legal-summary">{summary}</p>}
          </header>

          {needsReview && (
            <div className="legal-banner" role="note">
              <AlertTriangle size={16} aria-hidden="true" />
              <div>
                <strong>[Review before going live]</strong> This page is template copy. Have a lawyer review before
                exposing it on production. Track status in the admin console.
              </div>
            </div>
          )}

          <div className="legal-content">{children}</div>
        </article>
      </div>

      <footer className="legal-footer">
        <div className="container legal-footer-inner">
          <span>&copy; 2026 Rynora.</span>
          <span className="footer-bottom-links">
            <Link to="/trust">Trust</Link>
            <Link to="/privacy">Privacy</Link>
            <Link to="/terms">Terms</Link>
            <Link to="/security">Security</Link>
            <a href="mailto:support@rynora.app">support@rynora.app</a>
          </span>
        </div>
      </footer>
    </div>
  );
}
